import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { LogoIcon, LogoText } from '../components/Logo';

function Account() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['account', 'common']);
  const { language, setLanguage, availableLanguages } = useLanguage();
  const { theme, setTheme } = useTheme();

  const [newsletter, setNewsletter] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(true);

  // Password change
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');

  const isGoogleUser = user?.app_metadata?.provider === 'google';
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  // Load newsletter preference
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('newsletter_subscribed')
        .eq('id', user.id)
        .single();
      if (data) {
        setNewsletter(data.newsletter_subscribed || false);
      }
      setNewsletterLoading(false);
    };
    loadProfile();
  }, [user]);

  const handleNewsletterToggle = async () => {
    const newValue = !newsletter;
    setNewsletter(newValue);
    await supabase
      .from('profiles')
      .update({ newsletter_subscribed: newValue })
      .eq('id', user.id);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: t('auth:errorPasswordTooShort', { ns: 'auth' }) });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('auth:errorPasswordsDontMatch', { ns: 'auth' }) });
      return;
    }

    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setPasswordMessage({ type: 'error', text: error.message });
    } else {
      setPasswordMessage({ type: 'success', text: t('account:passwordUpdated') });
      setNewPassword('');
      setConfirmPassword('');
    }
    setPasswordLoading(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-anthracite-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-anthracite-900/95 backdrop-blur-sm border-b border-anthracite-700">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-3">
              <LogoIcon className="w-10 h-10" />
              <div>
                <LogoText size="text-xl" />
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('common:aiFootballPredictions')}</p>
              </div>
            </Link>
          </div>
          <Link
            to="/dashboard"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('common:backToPredictions')}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-12 px-4">
        <div className="max-w-2xl mx-auto space-y-6">

          <h2 className="text-2xl font-bold text-white">{t('account:title')}</h2>

          {/* Profile Info */}
          <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:profile')}</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('account:email')}</span>
                <span className="text-sm text-white">{user?.email}</span>
              </div>
              <div className="h-px bg-anthracite-700" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('account:signInMethod')}</span>
                <span className="text-sm text-white flex items-center gap-2">
                  {isGoogleUser ? (
                    <>
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t('account:google')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {t('account:emailMethod')}
                    </>
                  )}
                </span>
              </div>
              {memberSince && (
                <>
                  <div className="h-px bg-anthracite-700" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{t('account:memberSince')}</span>
                    <span className="text-sm text-white">{memberSince}</span>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Language Preference */}
          <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:language')}</h3>
            <div className="flex flex-wrap gap-2">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                    lang.code === language
                      ? 'bg-accent/20 text-accent border-accent/30'
                      : 'bg-anthracite-800 text-gray-400 border-anthracite-700 hover:border-accent/30 hover:text-white'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Theme Preference */}
          <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:theme')}</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setTheme('dark')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  theme === 'dark'
                    ? 'bg-accent/20 text-accent border-accent/30'
                    : 'bg-anthracite-800 text-gray-400 border-anthracite-700 hover:border-accent/30 hover:text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                {t('account:dark')}
              </button>
              <button
                onClick={() => setTheme('light')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  theme === 'light'
                    ? 'bg-accent/20 text-accent border-accent/30'
                    : 'bg-anthracite-800 text-gray-400 border-anthracite-700 hover:border-accent/30 hover:text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                {t('account:light')}
              </button>
            </div>
          </section>

          {/* Change Password — only for email/password users */}
          {!isGoogleUser && (
            <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:changePassword')}</h3>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {passwordMessage && (
                  <div className={`rounded-lg p-3 text-sm ${
                    passwordMessage.type === 'error'
                      ? 'bg-red-500/10 border border-red-500/50 text-red-400'
                      : 'bg-green-500/10 border border-green-500/50 text-green-400'
                  }`}>
                    {passwordMessage.text}
                  </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">{t('account:newPassword')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-anthracite-800 border border-anthracite-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                    placeholder={t('account:atLeast6Chars')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">{t('account:confirmNewPassword')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-anthracite-800 border border-anthracite-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                    placeholder={t('account:confirmPasswordPlaceholder')}
                  />
                </div>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-6 py-3 bg-accent hover:bg-accent-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? t('account:updating') : t('account:updatePassword')}
                </button>
              </form>
            </section>
          )}

          {/* Newsletter Preferences */}
          <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:newsletter')}</h3>
            {newsletterLoading ? (
              <div className="text-sm text-gray-500">{t('common:loading')}</div>
            ) : (
              <label className="flex items-start gap-4 cursor-pointer">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={newsletter}
                    onChange={handleNewsletterToggle}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-anthracite-700 peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:bg-accent transition-colors"></div>
                  <div className="absolute left-[2px] top-[2px] w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                </div>
                <div>
                  <span className="text-white font-medium text-sm">{t('account:pickOfTheDay')}</span>
                  <p className="text-gray-500 text-xs mt-1">
                    {t('account:newsletterDesc')}
                  </p>
                </div>
              </label>
            )}
          </section>

          {/* Account Actions */}
          <section className="bg-anthracite-900 rounded-2xl p-6 border border-anthracite-700">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">{t('account:accountActions')}</h3>
            <div className="space-y-3">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all border border-anthracite-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('common:signOut')}
              </button>

              {/* Delete Account */}
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-red-400/60 hover:text-red-400 hover:bg-red-500/5 transition-all border border-anthracite-700 hover:border-red-500/30"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {t('account:deleteAccount')}
                </button>
              ) : (
                <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/30 space-y-3">
                  <p className="text-sm text-red-400">
                    {t('account:deleteConfirmText').replace('<bold>', '').replace('</bold>', '')}
                  </p>
                  <input
                    type="text"
                    value={deleteText}
                    onChange={(e) => setDeleteText(e.target.value)}
                    className="w-full px-4 py-2 bg-anthracite-800 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                    placeholder={t('account:deleteConfirmPlaceholder')}
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}
                      className="flex-1 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white bg-anthracite-800 transition-colors"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      disabled={deleteText !== 'DELETE'}
                      className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-600 transition-colors"
                    >
                      {t('account:deletePermanently')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}

export default Account;
